import { useState, useEffect, useRef } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Loader2, Crown, Sparkles, TrendingUp, RefreshCw, Wallet, RefreshCcw } from 'lucide-react'
import { DataTable, SortableHeader } from '../ui/data-table'
import { useScrollRestoration } from '../../hooks/useScrollRestoration'
import { apiClient } from '../../lib/api'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'

interface UserMembershipInfo {
  user_id: string
  user_email?: string
  user_name?: string
  tier: string
  downloads_this_month: number
  free_downloads_per_month: number
  billing_cycle_end?: string
  credit_balance_usd?: number
  is_active: boolean
}

interface SyncResult {
  userId: string
  userName?: string
  syncedPurchases: Array<{
    purchase_id: string
    payment_intent_id: string
    amount_usd: number
    created: number
    status: string
    description: string | null
  }>
  extraDbTransactions: Array<{
    type: string
    id: string
    payment_intent_id: string
    amount_usd: number
    created_at: string | null
    description: string | null
  }>
  subscriptionStatus: {
    id: string
    status: string
    current_period_end: number
    cancel_at_period_end: boolean
    db_status: string
    matches_db: boolean
    error?: string
  } | null
  balance: {
    old_balance: number
    new_balance: number
    total_purchases: number
    total_deductions: number
    updated: boolean
  }
  message: string
}

interface AdminMembershipsProps {
  isActive?: boolean
}

export function AdminMemberships({ isActive = true }: AdminMembershipsProps) {
  useScrollRestoration('admin-memberships-table', isActive)
  const [memberships, setMemberships] = useState<UserMembershipInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 })
  const [pageCount, setPageCount] = useState(0)
  const [fixingBalances, setFixingBalances] = useState<Set<string>>(new Set())
  const [syncingStripe, setSyncingStripe] = useState<Set<string>>(new Set())
  const [syncResultDialog, setSyncResultDialog] = useState<SyncResult | null>(null)

  // Use a ref to track previous pagination to avoid unnecessary fetches
  const prevPaginationRef = useRef({ pageIndex: 0, pageSize: 10 })

  useEffect(() => {
    // Only load if pagination values actually changed
    if (
      prevPaginationRef.current.pageIndex !== pagination.pageIndex ||
      prevPaginationRef.current.pageSize !== pagination.pageSize
    ) {
      prevPaginationRef.current = pagination
      loadMemberships()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination])

  // Load memberships on initial mount
  useEffect(() => {
    loadMemberships()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadMemberships = async () => {
    try {
      setLoading(true)
      const page = pagination.pageIndex + 1 // API uses 1-based pages
      const response = await apiClient.admin.getAdminMemberships(page, pagination.pageSize)
      if (response.memberships) {
        setMemberships(response.memberships)
        if (response.pagination) {
          setPageCount(response.pagination.total_pages)
        }
      }
    } catch (error) {
      console.error('Error loading memberships:', error)
    } finally {
      setLoading(false)
    }
  }

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'book_giant':
        return <Crown className="h-4 w-4 text-yellow-500" />
      case 'plus':
        return <Sparkles className="h-4 w-4 text-purple-500" />
      default:
        return <TrendingUp className="h-4 w-4 text-blue-500" />
    }
  }

  const getTierBadge = (tier: string) => {
    const colors: Record<string, string> = {
      basic: 'bg-blue-500',
      plus: 'bg-purple-500',
      book_giant: 'bg-yellow-500',
    }
    
    return (
      <Badge className={`${colors[tier] || 'bg-gray-500'} text-white capitalize`}>
        {tier.replace('_', ' ')}
      </Badge>
    )
  }

  const handleFixBalance = async (userId: string) => {
    try {
      setFixingBalances(prev => new Set(prev).add(userId))
      const result = await apiClient.admin.fixUserBalance(userId)
      if (result.success) {
        alert(result.message || 'Balance fixed successfully')
        // Reload memberships to reflect any changes
        await loadMemberships()
      }
    } catch (error: any) {
      console.error('Failed to fix balance:', error)
      alert(error.message || 'Failed to fix balance')
    } finally {
      setFixingBalances(prev => {
        const next = new Set(prev)
        next.delete(userId)
        return next
      })
    }
  }

  const handleSyncStripe = async (userId: string, userName?: string) => {
    try {
      setSyncingStripe(prev => new Set(prev).add(userId))
      const result = await apiClient.admin.syncStripeTransactions(userId)
      if (result.success) {
        // Show dialog with sync results
        setSyncResultDialog({
          userId,
          userName,
          syncedPurchases: result.synced_purchases,
          extraDbTransactions: result.extra_db_transactions,
          subscriptionStatus: result.subscription_status,
          balance: result.balance,
          message: result.message,
        })
        // Reload memberships to reflect any changes
        await loadMemberships()
      }
    } catch (error: any) {
      console.error('Failed to sync Stripe:', error)
      alert(error.message || 'Failed to sync Stripe transactions')
    } finally {
      setSyncingStripe(prev => {
        const next = new Set(prev)
        next.delete(userId)
        return next
      })
    }
  }

  const columns: ColumnDef<UserMembershipInfo>[] = [
    {
      accessorKey: 'user_id',
      header: ({ column }) => <SortableHeader column={column}>User</SortableHeader>,
      cell: ({ row }) => (
        <div>
          <p className="font-medium">{row.original.user_name || 'Unknown'}</p>
          <p className="text-sm text-muted-foreground">{row.original.user_email || row.original.user_id.substring(0, 8) + '...'}</p>
        </div>
      ),
    },
    {
      accessorKey: 'tier',
      header: ({ column }) => <SortableHeader column={column}>Tier</SortableHeader>,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {getTierIcon(row.original.tier)}
          {getTierBadge(row.original.tier)}
        </div>
      ),
    },
    {
      accessorKey: 'credit_balance_usd',
      header: ({ column }) => <SortableHeader column={column}>Balance</SortableHeader>,
      cell: ({ row }) => (
        <div className="text-sm font-medium">
          ${(row.original.credit_balance_usd || 0).toFixed(2)}
        </div>
      ),
    },
    {
      accessorKey: 'downloads_this_month',
      header: 'Downloads',
      cell: ({ row }) => (
        <div className="text-sm">
          <p>{row.original.downloads_this_month} / {row.original.free_downloads_per_month}</p>
        </div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'billing_cycle_end',
      header: ({ column }) => <SortableHeader column={column}>Cycle Ends</SortableHeader>,
      cell: ({ row }) => (
        <p className="text-sm">
          {row.original.billing_cycle_end 
            ? new Date(row.original.billing_cycle_end).toLocaleDateString()
            : '—'}
        </p>
      ),
    },
    {
      accessorKey: 'is_active',
      header: ({ column }) => <SortableHeader column={column}>Status</SortableHeader>,
      cell: ({ row }) => (
        <Badge variant={row.original.is_active ? 'default' : 'secondary'}>
          {row.original.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const membership = row.original
        const isFixing = fixingBalances.has(membership.user_id)
        const isSyncing = syncingStripe.has(membership.user_id)
        
        return (
          <div className="actions-cell flex items-center gap-1">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleFixBalance(membership.user_id)}
              disabled={isFixing}
              className="fix-balance-btn"
            >
              {isFixing ? (
                <>
                  <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                  Fixing...
                </>
              ) : (
                <>
                  <Wallet className="h-3 w-3 mr-2" />
                  Fix Balance
                </>
              )}
            </Button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleSyncStripe(membership.user_id, membership.user_name)}
                    disabled={isSyncing}
                    className="sync-stripe-btn"
                  >
                    {isSyncing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCcw className="h-4 w-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Sync Stripe transactions and membership</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )
      },
      enableSorting: false,
    },
  ]

  return (
    <>
    <Card className="admin-memberships-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>User Memberships</CardTitle>
            <CardDescription>View and manage user subscription tiers and usage</CardDescription>
          </div>
          <Button onClick={loadMemberships} size="sm" variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                  <p className="text-2xl font-bold">
                    {loading ? '...' : (pagination.pageIndex * pagination.pageSize + memberships.length)}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Basic Tier</p>
                  <p className="text-2xl font-bold">
                    {memberships.filter(m => m.tier === 'basic').length}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Plus Tier</p>
                  <p className="text-2xl font-bold">
                    {memberships.filter(m => m.tier === 'plus').length}
                  </p>
                </div>
                <Sparkles className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Book Giant</p>
                  <p className="text-2xl font-bold">
                    {memberships.filter(m => m.tier === 'book_giant').length}
                  </p>
                </div>
                <Crown className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Memberships Table */}
        {loading && memberships.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="memberships-table-container space-y-4">
            <DataTable
              columns={columns}
              data={memberships}
              searchColumn="user_id"
              searchPlaceholder="Search by email, name, or user ID..."
              enableRowSelection={false}
              enableColumnVisibility={true}
              enablePagination={true}
              pageSize={10}
              manualPagination={true}
              pageCount={pageCount}
              pagination={pagination}
              onPaginationChange={setPagination}
            />
          </div>
        )}
      </CardContent>
    </Card>

    {/* Sync Result Dialog */}
    <Dialog open={!!syncResultDialog} onOpenChange={(open) => !open && setSyncResultDialog(null)}>
      <DialogContent className="sync-result-dialog max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Stripe Sync Results</DialogTitle>
          <DialogDescription>
            {syncResultDialog?.userName || syncResultDialog?.userId}
          </DialogDescription>
        </DialogHeader>
        
        {syncResultDialog && (
          <div className="sync-result-content space-y-4">
            {/* Summary Message */}
            <div className="summary-section p-3 bg-muted rounded-lg">
              <p className="text-sm">{syncResultDialog.message}</p>
            </div>

            {/* Balance Info */}
            {syncResultDialog.balance && (
              <div className="balance-section">
                <h4 className="font-medium mb-2">Balance</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Total Purchases:</div>
                  <div className="font-mono">${syncResultDialog.balance.total_purchases.toFixed(2)}</div>
                  <div>Total Deductions:</div>
                  <div className="font-mono">${syncResultDialog.balance.total_deductions.toFixed(2)}</div>
                  <div>Previous Balance:</div>
                  <div className="font-mono">${syncResultDialog.balance.old_balance.toFixed(2)}</div>
                  <div>Current Balance:</div>
                  <div className="font-mono font-medium">${syncResultDialog.balance.new_balance.toFixed(2)}</div>
                  {syncResultDialog.balance.updated && (
                    <div className="col-span-2 text-green-600 text-xs">Balance was updated</div>
                  )}
                </div>
              </div>
            )}

            {/* Synced Purchases */}
            {syncResultDialog.syncedPurchases.length > 0 && (
              <div className="synced-purchases-section">
                <h4 className="font-medium mb-2 text-green-600">
                  Synced from Stripe ({syncResultDialog.syncedPurchases.length})
                </h4>
                <div className="space-y-2">
                  {syncResultDialog.syncedPurchases.map((p) => (
                    <div key={p.purchase_id} className="text-sm p-3 bg-green-50 dark:bg-green-950 rounded border border-green-200 dark:border-green-800">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-mono font-medium">${p.amount_usd.toFixed(2)}</span>
                        <Badge variant={p.status === 'succeeded' || p.status === 'paid' ? 'default' : 'secondary'} className="text-xs">
                          {p.status}
                        </Badge>
                      </div>
                      <div className="text-muted-foreground text-xs space-y-0.5">
                        <div>
                          <span className="font-medium">Date:</span>{' '}
                          {new Date(p.created * 1000).toLocaleString()}
                        </div>
                        {p.description && (
                          <div>
                            <span className="font-medium">Description:</span>{' '}
                            {p.description}
                          </div>
                        )}
                        <div className="truncate">
                          <span className="font-medium">Payment Intent:</span>{' '}
                          <span className="font-mono">{p.payment_intent_id}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Subscription Status */}
            {syncResultDialog.subscriptionStatus && (
              <div className="subscription-section">
                <h4 className="font-medium mb-2">Subscription Status</h4>
                <div className="text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    <span>Stripe Status:</span>
                    <Badge variant={syncResultDialog.subscriptionStatus.status === 'active' ? 'default' : 'secondary'}>
                      {syncResultDialog.subscriptionStatus.status || 'N/A'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>DB Status:</span>
                    <Badge variant="outline">{syncResultDialog.subscriptionStatus.db_status || 'N/A'}</Badge>
                  </div>
                  {!syncResultDialog.subscriptionStatus.matches_db && (
                    <div className="text-yellow-600 text-xs">DB status was updated to match Stripe</div>
                  )}
                  {syncResultDialog.subscriptionStatus.error && (
                    <div className="text-red-600 text-xs">Error: {syncResultDialog.subscriptionStatus.error}</div>
                  )}
                </div>
              </div>
            )}

            {/* Extra DB Transactions */}
            {syncResultDialog.extraDbTransactions.length > 0 && (
              <div className="extra-transactions-section">
                <h4 className="font-medium mb-2 text-yellow-600">
                  Extra Transactions in DB ({syncResultDialog.extraDbTransactions.length})
                </h4>
                <p className="text-xs text-muted-foreground mb-2">
                  These transactions exist in the database but were not found in Stripe.
                </p>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {syncResultDialog.extraDbTransactions.map((t) => (
                    <div key={t.id} className="text-sm p-2 bg-yellow-50 dark:bg-yellow-950 rounded">
                      <div className="flex items-center justify-between">
                        <span className="font-mono">${t.amount_usd.toFixed(2)}</span>
                        <Badge variant="outline" className="text-xs">{t.type}</Badge>
                      </div>
                      <div className="text-muted-foreground text-xs mt-1">
                        <div>ID: {t.id}</div>
                        <div>Payment Intent: {t.payment_intent_id || 'N/A'}</div>
                        {t.created_at && <div>Created: {new Date(t.created_at).toLocaleString()}</div>}
                        {t.description && <div className="truncate">Desc: {t.description}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No Extra Transactions */}
            {syncResultDialog.extraDbTransactions.length === 0 && (
              <div className="text-sm text-muted-foreground">
                ✓ No extra transactions found in DB
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  )
}

